import { z } from 'zod';
import { TICKET_TYPE } from '#/components/ui/tickets/config';
import { VehicleTypeSchema } from '../compulsory-insurance-penalty/model';

export { TICKET_TYPE };

export type EnrollmentType =
  | TICKET_TYPE.COMPULSORY_INSURANCE_PENALTY_TICKETS
  | TICKET_TYPE.ETC_TICKETS
  | TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKETS
  | TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_TICKETS;

export const ENROLLMENT_TYPE_VALUE = [
  TICKET_TYPE.COMPULSORY_INSURANCE_PENALTY_TICKETS,
  TICKET_TYPE.ETC_TICKETS,
  TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKETS,
  TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_TICKETS,
];

const EnrollmentTypeSchema = z.enum(ENROLLMENT_TYPE_VALUE);

type EnrollmentStateValue = 'ACTIVE' | 'INACTIVE';

const ENROLLMENT_STATE_VALUE: EnrollmentStateValue[] = ['ACTIVE', 'INACTIVE'];

export const EnrollmentState = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
};

export const ENROLLMENT_STATE_POLICY = {
  ACTIVE: {
    text: '啟用',
  },
  INACTIVE: {
    text: '停用',
  },
} as const satisfies Record<EnrollmentStateValue, { text: string }>;

export type PayIntervalValue =
  | 'WEEKLY'
  | 'MONTHLY' // 過去的值，無法設定
  | 'SEMI_MONTHLY'
  | 'QUARTERLY'
  | 'YEARLY';

export const PAY_INTERVAL_VALUE: PayIntervalValue[] = [
  'WEEKLY',
  'MONTHLY',
  'SEMI_MONTHLY',
  'QUARTERLY',
  'YEARLY',
];

export const PAY_INTERVAL_LABEL = {
  WEEKLY: '每週',
  MONTHLY: '每月',
  SEMI_MONTHLY: '每月兩次',
  QUARTERLY: '每季',
  YEARLY: '每年',
} as const satisfies Record<PayIntervalValue, string>;

const EnrollmentAccountSchema = z.object({
  id: z.string(),
  email: z.string(),
});

const EnrollmentSchema = z.object({
  id: z.string(),
  account: EnrollmentAccountSchema,
  plateNumber: z.string(),
  idNumber: z.string().optional(),
  taxIdNumber: z.string().optional(),
  state: z.enum(ENROLLMENT_STATE_VALUE),
  isSuspended: z.boolean(),
  suspendedReason: z.string().optional(),
  payInterval: z.enum(PAY_INTERVAL_VALUE),
  createDateTime: z.string(),
  updateDateTime: z.string(),
});

const PenaltyEnrollmentSchema = EnrollmentSchema.extend({
  vehicleType: VehicleTypeSchema,
});

export type Enrollment = z.infer<typeof EnrollmentSchema>;

const EnrollmentSummarySchema = z.object({
  id: EnrollmentSchema.shape.id,
  email: EnrollmentSchema.shape.account.shape.email,
  plateNumber: EnrollmentSchema.shape.plateNumber,
  idNumber: EnrollmentSchema.shape.idNumber,
  taxIdNumber: EnrollmentSchema.shape.taxIdNumber,
  payInterval: EnrollmentSchema.shape.payInterval,
  createDateTime: EnrollmentSchema.shape.createDateTime,
  updateDateTime: EnrollmentSchema.shape.updateDateTime,
  state: EnrollmentSchema.shape.state,
  isSuspended: EnrollmentSchema.shape.isSuspended,
  type: EnrollmentTypeSchema,
  vehicleType: PenaltyEnrollmentSchema.shape.vehicleType.optional(),
});

type EnrollmentSummary = z.infer<typeof EnrollmentSummarySchema>;

export {
  EnrollmentAccountSchema,
  EnrollmentSchema,
  PenaltyEnrollmentSchema,
  EnrollmentSummarySchema,
};

export type { EnrollmentSummary };
